import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ProductCard } from '../components/products/ProductCard';
import { isFirebaseConfigured } from '../firebase/client';
import { listActiveProducts } from '../lib/products/productQueries';
import { groupProductsByTypeSections } from '../lib/products/productModel';
import site from '../lib/seo/site.json';

/**
 * Landing page for Audio at Play.
 */
export function HomePage() {
  const [products, setProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState('');
  const maxProductsPerSection = 3;

  useEffect(() => {
    let mounted = true;

    async function loadProducts() {
      try {
        const nextProducts = await listActiveProducts();
        if (mounted) {
          setProducts(nextProducts);
          setProductsError('');
        }
      } catch (loadError) {
        if (mounted) {
          setProductsError(loadError.message || 'Failed to load homepage products.');
        }
      } finally {
        if (mounted) {
          setIsLoadingProducts(false);
        }
      }
    }

    loadProducts();
    return () => {
      mounted = false;
    };
  }, []);

  const freeSoundProducts = useMemo(() => {
    const freeNativeProducts = (products || []).filter(
      (product) =>
        product?.isFree === true && (product?.listingType === 'native' || product?.listingType === 'free')
    );
    const featuredFree = freeNativeProducts.filter((product) => product?.isHomepageFeatured === true);
    const prioritized = featuredFree.length ? featuredFree : freeNativeProducts;
    return prioritized.slice(0, maxProductsPerSection);
  }, [products]);

  const homepageSections = useMemo(
    () =>
      groupProductsByTypeSections(products, {
        includeEmpty: false,
        preferFeatured: true,
        maxProductsPerSection,
      }),
    [maxProductsPerSection, products]
  );
  const orderedHomepageSections = useMemo(() => {
    const sectionOrder = [
      'single-sounds',
      'sound-packs',
      'ableton-sound-packs',
      'm4l',
      'instrument',
      'other',
    ];
    const priorityByKey = new Map(sectionOrder.map((key, index) => [key, index]));

    return [...homepageSections].sort((left, right) => {
      const leftPriority = priorityByKey.has(left.key)
        ? priorityByKey.get(left.key)
        : Number.MAX_SAFE_INTEGER;
      const rightPriority = priorityByKey.has(right.key)
        ? priorityByKey.get(right.key)
        : Number.MAX_SAFE_INTEGER;
      return leftPriority - rightPriority;
    });
  }, [homepageSections]);
  const topHomepageSections = useMemo(
    () =>
      orderedHomepageSections.filter(
        (section) => section.key === 'single-sounds' || section.key === 'sound-packs'
      ),
    [orderedHomepageSections]
  );
  const remainingHomepageSections = useMemo(
    () =>
      orderedHomepageSections.filter(
        (section) => section.key !== 'single-sounds' && section.key !== 'sound-packs'
      ),
    [orderedHomepageSections]
  );

  return (
    <section className="page grid">
      <div className="hero glass">
        <h1 className="home-hero-title">Audio at Play</h1>
        <p className="home-hero-copy hero-subtitle">{site.tagline}</p>
        <p>{site.collectionDescription}</p>
        {!isFirebaseConfigured ? (
          <div className="notice error">
            Catalogue and checkout are currently unavailable.
          </div>
        ) : null}
        <div className="btn-row">
          <Link className="btn btn-primary" to="/shop">
            Explore the sounds
          </Link>
          <Link className="btn btn-secondary" to="/library">
            Open My Library
          </Link>
        </div>
      </div>

      {productsError ? <div className="notice error">{productsError}</div> : null}
      {isLoadingProducts ? <div className="notice">Loading homepage products...</div> : null}

      {!isLoadingProducts && !productsError ? (
        <div className="grid home-product-sections">
          {topHomepageSections.map((section) => (
            <article className="card home-product-section" key={section.key}>
              <h3>{section.title}</h3>
              <p>{section.description}</p>

              {section.products.length ? (
                <div className="grid products home-product-cards">
                  {section.products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="notice">No active products are available in this section yet.</div>
              )}
              <div className="btn-row">
                <Link className="btn btn-ghost" to={`/browse/${section.key}`}>
                  View More {section.title}
                </Link>
              </div>
            </article>
          ))}

          {freeSoundProducts.length ? <article className="card home-product-section" key="free-sounds">
            <h3>Free Sounds</h3>
            <p>Free sounds. Google sign-in is required to download.</p>

            {freeSoundProducts.length ? (
              <div className="grid products home-product-cards">
                {freeSoundProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="notice">No active free sounds are available right now.</div>
            )}
            <div className="btn-row">
              <Link className="btn btn-ghost" to="/browse/free-sounds">
                View More Free Sounds
              </Link>
            </div>
          </article> : null}

          {remainingHomepageSections.map((section) => (
            <article className="card home-product-section" key={section.key}>
              <h3>{section.title}</h3>
              <p>{section.description}</p>

              {section.products.length ? (
                <div className="grid products home-product-cards">
                  {section.products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              ) : (
                <div className="notice">No active products are available in this section yet.</div>
              )}
              <div className="btn-row">
                <Link className="btn btn-ghost" to={`/browse/${section.key}`}>
                  View More {section.title}
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}
